<script setup>
// Shared searchable picker base. ClientPicker and BucketPicker both compose it
// so the Client → Bucket hierarchy is ONE component, everywhere (§8.5). Keyboard
// accessible, popover floats (shadow, §4). Items: [{ value, label, meta? }].
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue'
import LField from './Field.vue'
import LIcon from '../base/Icon.vue'

const props = defineProps({
	label: { type: String, default: null },
	hint: { type: String, default: null },
	items: { type: Array, default: () => [] },
	placeholder: { type: String, default: 'Select…' },
	searchPlaceholder: { type: String, default: 'Search…' },
	disabled: { type: Boolean, default: false },
	disabledHint: { type: String, default: null },
})
const model = defineModel({ default: null })

const open = ref(false)
const query = ref('')
const searchEl = ref(null)
const rootEl = ref(null)
const active = ref(0)

const filtered = computed(() =>
{
	const q = query.value.trim().toLowerCase()
	if (!q) return props.items
	return props.items.filter((i) => i.label.toLowerCase().includes(q) || i.meta?.toLowerCase().includes(q))
})
const selected = computed(() => props.items.find((i) => i.value === model.value) || null)

// Close on any pointerdown outside the widget. This is more robust than a
// focusout handler, which races with the search input auto-focus (the focus
// transition can report a null relatedTarget and slam the popover shut the
// instant it opens).
function onDocPointer(e)
{
	if (rootEl.value && !rootEl.value.contains(e.target)) open.value = false
}
watch(open, async (v) =>
{
	if (v)
	{
		query.value = ''
		active.value = 0
		document.addEventListener('pointerdown', onDocPointer, true)
		await nextTick()
		searchEl.value?.focus()
	} else
	{
		document.removeEventListener('pointerdown', onDocPointer, true)
	}
})
onBeforeUnmount(() => document.removeEventListener('pointerdown', onDocPointer, true))

function toggle()
{
	if (props.disabled) return
	open.value = !open.value
}
function pick(item)
{
	model.value = item.value
	open.value = false
}
function onKey(e)
{
	if (e.key === 'ArrowDown') { active.value = Math.min(active.value + 1, filtered.value.length - 1); e.preventDefault() }
	else if (e.key === 'ArrowUp') { active.value = Math.max(active.value - 1, 0); e.preventDefault() }
	else if (e.key === 'Enter') { if (filtered.value[active.value]) pick(filtered.value[active.value]); e.preventDefault() }
	else if (e.key === 'Escape') { open.value = false }
}
</script>

<template>
	<LField :label="label" :hint="disabled ? (disabledHint || hint) : hint" v-slot="{ id }">
		<div ref="rootEl" class="combo">
			<button :id="id" type="button" class="trigger" :class="{ empty: !selected, disabled }" :disabled="disabled"
				:aria-expanded="open" aria-haspopup="listbox" @click="toggle">
				<LIcon name="lock" v-if="disabled" :size="14" class="lead" aria-hidden="true" />
				<span class="val">{{ selected ? selected.label : placeholder }}</span>
				<LIcon name="chevron-down" :size="15" class="chev" aria-hidden="true" />
			</button>

			<div v-if="open && !disabled" class="pop" role="listbox">
				<div class="search">
					<LIcon name="magnifying-glass" :size="15" aria-hidden="true" />
					<input ref="searchEl" v-model="query" :placeholder="searchPlaceholder" aria-label="Filter options"
						@keydown="onKey" />
				</div>
				<ul class="list">
					<li v-for="(it, i) in filtered" :key="it.value" role="option" :aria-selected="it.value === model"
						class="opt" :class="{ active: i === active }" @mouseenter="active = i"
						@mousedown.prevent="pick(it)">
						<div class="opt-main">
							<span class="opt-label">{{ it.label }}</span>
							<span v-if="it.meta" class="opt-meta">{{ it.meta }}</span>
						</div>
						<LIcon name="check" v-if="it.value === model" :size="14" class="opt-tick" />
					</li>
					<li v-if="!filtered.length" class="empty-opt">No matches</li>
				</ul>
			</div>
		</div>
	</LField>
</template>

<style scoped>
.combo
{
	position: relative;
}

.trigger
{
	display: flex;
	align-items: center;
	gap: var(--sp-2);
	width: 100%;
	font-family: var(--font-ui);
	font-size: var(--text-base);
	color: var(--text);
	padding: 9px 12px;
	border: 1px solid var(--line);
	border-radius: var(--r);
	background: var(--surface);
	min-height: var(--target-min);
	cursor: pointer;
	text-align: left;
}

.trigger:hover:not(.disabled)
{
	border-color: var(--line-strong);
}

.trigger.empty .val
{
	color: var(--text-3);
}

.trigger.disabled
{
	background: var(--surface-sunken);
	color: var(--text-3);
	cursor: not-allowed;
}

.lead
{
	color: var(--icon);
	flex: none;
}

.val
{
	flex: 1;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.chev
{
	color: var(--icon);
	flex: none;
}

.pop
{
	position: absolute;
	z-index: 60;
	top: calc(100% + 6px);
	left: 0;
	right: 0;
	background: var(--surface);
	border: 1px solid var(--line);
	border-radius: var(--r);
	box-shadow: var(--shadow);
	overflow: hidden;
}

.search
{
	display: flex;
	align-items: center;
	gap: var(--sp-2);
	padding: 8px 10px;
	border-bottom: 1px solid var(--line-soft);
}

.search :deep(i)
{
	color: var(--icon);
}

.search input
{
	flex: 1;
	border: 0;
	background: none;
	font-family: var(--font-ui);
	font-size: var(--text-sm);
	color: var(--text);
}

.search input:focus-visible
{
	outline: none;
}

.list
{
	list-style: none;
	max-height: 240px;
	overflow-y: auto;
	padding: 5px;
}

.opt
{
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: var(--sp-3);
	padding: 8px 10px;
	border-radius: var(--r-sm);
	cursor: pointer;
	min-height: var(--target-min);
}

.opt.active
{
	background: var(--surface-sunken);
}

.opt-main
{
	display: flex;
	flex-direction: column;
	min-width: 0;
}

.opt-label
{
	font-size: var(--text-base);
	color: var(--text);
}

.opt-meta
{
	font-size: var(--text-chip);
	color: var(--text-3);
}

.opt-tick
{
	color: var(--interactive-text);
	flex: none;
}

.empty-opt
{
	padding: 12px 10px;
	font-size: var(--text-sm);
	color: var(--text-3);
	text-align: center;
}
</style>
